import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Update } from 'src/app/interfaces/update';
import { UpdateService } from 'src/app/services/update.service';
import { HttpParams } from '@angular/common/http';
import { WidgetService } from 'src/app/services/widget.service';
import { ProjectAboutUsComponent } from '../tabs/project-about-us/project-about-us.component';
import { TranslateService } from '@ngx-translate/core';
import { DateTypePipe } from 'src/app/pipes/date-type.pipe';
import { EmbedComponent } from '../../../components/embed/embed.component';
import { ProjectStore } from '../project.store';

@Component({
  standalone: true,
  selector: 'app-project-embed',
  templateUrl: './project-embed.page.html',

  imports: [EmbedComponent],
  providers: [DateTypePipe],
})
export class ProjectEmbedPage implements OnInit {
  private readonly updateService = inject(UpdateService);
  private readonly route = inject(ActivatedRoute);
  private readonly widgetService = inject(WidgetService);
  private readonly translate = inject(TranslateService);
  private readonly dateTypePipe = inject(DateTypePipe);

  updates = signal<Update[] | undefined>(undefined);
  header: string;
  theme: string;
  info: string;
  loading = signal(true);

  private readonly projectStore = inject(ProjectStore);
  project = computed(() => this.projectStore.project());
  projectHasStarted = computed(() => this.projectStore.projectHasStarted());

  logoUrl = computed(() => {
    const p = this.project();
    if (!p) return '';
    return p.logoThumbnails?.small || p.customer?.logoThumbnails?.small || '';
  });

  coverImageUrl = computed(() => {
    const p = this.project();
    return p?.coverImageThumbnails?.medium || '';
  });

  metaLines = computed(() => {
    const p = this.project();
    if (!p) return [];
    const lines: string[] = [];

    const startedKey = this.projectHasStarted()
      ? 'project.home.started'
      : 'project.home.will_start';
    const dateStr = this.dateTypePipe.transform(p.startDate, p.startType);
    lines.push(`${this.translate.instant(startedKey)} ${dateStr}`);

    const followersKey =
      p.amountOfFollowers === 1
        ? 'project.home.followers.singular'
        : 'project.home.followers.plural';
    lines.push(
      this.translate.instant(followersKey, { followers: p.amountOfFollowers })
    );

    return lines;
  });

  infoComponent = computed(() => ProjectAboutUsComponent);

  infoComponentProps = computed(() => ({
    project: this.project(),
    isModal: true,
  }));

  constructor() {
    effect(async () => {
      if (this.project()) {
        this.widgetService.initProject(this.project());
        const params = new HttpParams().set('perPage', '21');
        const result = await this.updateService.getAllUpdates(
          this.project(),
          params
        );
        this.updates.set(result.member);
        this.loading.set(false);
      }
    });
  }

  ngOnInit() {
    this.header = this.route.snapshot.queryParams.header;
    this.theme = this.route.snapshot.queryParams.theme;
    this.info = this.route.snapshot.queryParams.info;
  }
}
